import { type JSX, Show } from 'solid-js';
import { t, tPlural } from '../../i18n';
import { useCardMove } from '../../lib/card-move';
import type { MovePreview, MovePreviewTarget } from '../../lib/move-calls';
import type { MoveSubject } from '../../lib/move-items';
import type { MenuOption } from '../../lib/move-to-menu';

// One machine in Move to (#6771, prototype menuHTML's `.mvopt`, `:1907-1910`): its name in
// bold with its 8-char id, the mono "CPU a% → b%" from the server's preview (omitted when the
// preview has no reading), and a small line: the refusal's sentence, a move's wait on this
// machine counting down, or "<source> goes down to N% CPU · uses C move(s) of L left", L the
// lower of the two machines' moves left. A refusing option is `aria-disabled`.

export function MoveOption(props: {
  readonly subject: MoveSubject;
  readonly option: MenuOption<MovePreviewTarget>;
  readonly preview: MovePreview;
  readonly lit: boolean;
  readonly onPick: () => void;
}): JSX.Element {
  const m = useCardMove();
  const okLine = (): string => {
    const p = props.preview;
    const cost = p.cost;
    const left = Math.min(props.option.target.left_after + cost, p.source.left);
    const src = p.source.cpu;
    return src === null
      ? tPlural('fleet.move.option_ok_nocpu', cost, { count: cost, left })
      : tPlural('fleet.move.option_ok', cost, {
          name: m?.page.boxOf(props.subject.sourceId)?.display_name ?? '',
          cpu: Math.round(src.after),
          count: cost,
          left,
        });
  };
  /** No projection: the machine's own CPU reading, before only ("CPU a%"), or nothing. */
  const cpuNow = (): string | null => {
    const ratio = m?.page.boxOf(props.option.machine.id)?.verdict.signals.cpu.ratio ?? null;
    return ratio === null
      ? null
      : t('fleet.move.option_cpu_now', { before: Math.round(ratio * 100) });
  };
  const wait = (): string | null => {
    const w = m?.page.waitFor(props.subject.key) ?? null;
    return w !== null && w.targetId === props.option.machine.id ? w.text : null;
  };
  return (
    <button
      type="button"
      class="move-opt"
      classList={{ 'move-opt--lit': props.lit }}
      aria-disabled={props.option.canTake ? undefined : 'true'}
      onClick={() => props.onPick()}
    >
      <span class="move-opt__name">
        <b>{props.option.machine.name}</b>{' '}
        <span class="move-opt__id">{props.option.machine.shortId}</span>
      </span>
      <span class="move-opt__cpu">
        <Show when={props.option.target.cpu} fallback={cpuNow()}>
          {(c) =>
            t('fleet.move.option_cpu', {
              before: Math.round(c().before),
              after: Math.round(c().after),
            })
          }
        </Show>
      </span>
      <small class="move-opt__line">{wait() ?? props.option.why ?? okLine()}</small>
    </button>
  );
}
